
// require sources
import _Quill from 'quill';

const Quill = window.Quill || _Quill;
const defaultOptions = {
  theme: 'snow',
  boundary: document.body,
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ['clean'],
      ['link', 'image', 'video'],
    ],
  },
  placeholder: 'Insert text here ...',
  readOnly: false,
};

// export
export default {
  name: 'QuillEditor',
  props: {
    content: String,
    value: String,
    disabled: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    globalOptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      quillOptions: {},
      quillContent: '',
      defaultOptions,
    };
  },
  watch: {
    // Watch content change
    content(newVal, _oldVal) {
      if (this.quill) {
        if (newVal && newVal !== this.quillContent) {
          this.pasteHTML(newVal);
        } else if (!newVal) {
          this.quill.setText('');
        }
      }
    },
    // Watch content change
    value(newVal, _oldVal) {
      if (this.quill) {
        if (newVal && newVal !== this.quillContent) {
          this.pasteHTML(newVal);
        } else if (!newVal) {
          this.quill.setText('');
        }
      }
    },
    // Watch disabled change
    disabled(newVal, _oldVal) {
      if (this.quill) {
        this.quill.enable(!newVal);
      }
    },
  },
  mounted() {
    this.initialize();
  },
  beforeDestroy() {
    this.quill = null;
    delete this.quill;
  },
  methods: {
    // Init Quill instance
    initialize() {
      if (this.$el) {
        // Options
        this.quillOptions = Object.assign({}, this.defaultOptions, this.globalOptions, this.options);

        // Instance
        this.quill = new Quill(this.$refs.editor, this.quillOptions);

        this.quill.enable(false);

        // Set editor content
        if (this.value || this.content) {
          this.pasteHTML(this.value || this.content, false);
        }

        // Disabled editor
        if (!this.disabled) {
          this.quill.enable(true);
        }

        // Mark model as touched if editor lost focus
        this.quill.on('selection-change', (range) => {
          if (!range) {
            this.$emit('blur', this.quill);
          } else {
            this.$emit('focus', this.quill);
          }
        });

        // Update model if text changes
        this.quill.on('text-change', () => {
          let html = this.quill.getSemanticHTML();
          // Adding a whitespace character to empty paragraphs to ensure that they
          // render as a empty linebreak - just like they do in the editor.
          html = html.replaceAll('<p></p>', '<p>&nbsp;</p>');
          const quill = this.quill;
          const text = this.quill.getText();
          if (html === '<p>&nbsp;</p>') html = '';
          this.quillContent = html;
          this.$emit('input', this.quillContent);
          this.$emit('change', { html, text, quill });
        });

        // Emit ready event
        this.$emit('ready', this.quill);
      }
    },
    // Convert modelValue HTML to Delta and replace editor content
    pasteHTML(value, setContent = true, isSilent = false) {
      // Removing added whitespace characters, so the user doesn't have to remove the
      // whitespace char before removing the empty linebreak.
      const content = value.replace(/<p>(&nbsp;|\s)<\/p>/gm, '<p></p>');
      if (setContent) this.quillContent = content ?? '';
      const delta = this.quill.clipboard.convert({ html: this.quillContent });
      this.quill.setContents(delta, isSilent ? 'silent' : 'api');
    },
  },
};
